import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { LevelState, ImageInfo } from '../../../services/image-processing-tool.service';
import { ThumbnailItemComponent } from '../thumbnail-item/thumbnail-item.component';
import { ImageViewerComponent } from '../image-viewer/image-viewer.component';

@Component({
  selector: 'app-level-pane',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatMenuModule,
    MatBadgeModule,
    ThumbnailItemComponent,
    ImageViewerComponent
  ],
  templateUrl: './level-pane.component.html',
  styleUrls: ['./level-pane.component.scss']
})
export class LevelPaneComponent implements OnInit, OnDestroy, OnChanges {
  @Input() level: number = 1;
  @Input() levelState: LevelState | undefined;
  @Input() navPaneWidth: number = 25;
  @Input() isFocused: boolean = false;
  @Input() focusedPaneType: 'nav' | 'work' | null = null;
  @Input() isLastLevel: boolean = false;
  
  @Output() navPaneResized = new EventEmitter<number>();
  @Output() paneFocused = new EventEmitter<'nav' | 'work'>();
  @Output() filesDropped = new EventEmitter<FileList>();
  @Output() cutRequested = new EventEmitter<{ imageId: string; cutLines: number[]; cutType: 'vertical' | 'horizontal' }>();
  @Output() cropRequested = new EventEmitter<{ imageId: string; cropRect: { x: number; y: number; width: number; height: number } }>();
  @Output() joinRequested = new EventEmitter<'vertical' | 'horizontal'>();
  @Output() undoJoinRequested = new EventEmitter<void>();
  @Output() moveToNextLevel = new EventEmitter<void>();
  @Output() deleteRequested = new EventEmitter<void>();
  @Output() deleteImageRequested = new EventEmitter<string>(); // Single image delete by ID
  @Output() deleteAllRequested = new EventEmitter<void>();
  @Output() undoRequested = new EventEmitter<void>();
  @Output() redoRequested = new EventEmitter<void>();
  @Output() saveRequested = new EventEmitter<void>();
  @Output() uploadGcsRequested = new EventEmitter<void>();
  @Output() pinToggled = new EventEmitter<boolean>();
  @Output() selectAllChanged = new EventEmitter<boolean>();
  @Output() openInWindowsViewer = new EventEmitter<string>();
  @Output() debugLog = new EventEmitter<string>();
  
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('navPane') navPane!: ElementRef<HTMLDivElement>;
  @ViewChild('workPane') workPane!: ElementRef<HTMLDivElement>;
  
  // Current image being viewed in work area
  currentImageId: string | null = null;
  currentImage: ImageInfo | null = null;
  
  // Navigation pane resize
  isResizing = false;
  startX = 0;
  startWidth = 0;
  
  // Drag over state
  isDragOver = false;
  
  // Selection tracking
  selectedThumbnailIndex = 0;
  
  // Modes - matching Python UI
  cropMode = false;
  markMode: 'vertical' | 'horizontal' | null = null;
  panMode = false;
  isPinned = false;
  
  // Zoom level
  zoomLevel = 1.0;
  
  // Marked lines - stores both position and type
  markedLines: { position: number; type: 'vertical' | 'horizontal' }[] = [];
  
  constructor(private cdr: ChangeDetectorRef) {}
  
  ngOnInit(): void {
    // Set initial current image if available
    if (this.levelState?.images?.length) {
      this.setCurrentImage(this.levelState.images[0].id);
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    // React to levelState changes
    if (changes['levelState']) {
      const previousValue = changes['levelState'].previousValue;
      const currentValue = changes['levelState'].currentValue;
      
      // Log the change for debugging
      if (currentValue?.images?.length) {
        console.log(`[LEVEL-PANE] Level ${this.level} state changed, images: ${currentValue.images.length}, currentImageId: ${this.currentImageId}`);
      }
      
      if (this.levelState?.images?.length) {
        // If no current image or current image is no longer in the list, auto-select first
        // This matches Python UI's auto_select_first_image behavior
        if (!this.currentImageId || !this.levelState.images.find(img => img.id === this.currentImageId)) {
          console.log(`[LEVEL-PANE] Level ${this.level} auto-selecting first image`);
          this.autoSelectFirstImage();
        } else {
          // Refresh current image reference
          this.currentImage = this.levelState.images.find(img => img.id === this.currentImageId) || null;
          console.log(`[LEVEL-PANE] Level ${this.level} refreshing current image: ${this.currentImage?.filename}`);
        }
      } else {
        // Clear selection when level is empty (like Python's clear_navigation)
        this.clearCurrentSelection();
      }
      this.cdr.detectChanges();
    }
  }
  
  ngOnDestroy(): void {
    // Cleanup
  }
  
  // ==================== Image Selection ====================
  
  /**
   * Auto-select the first visible image in the navigation pane.
   * This is called after cut/join/load operations to automatically display
   * the first result in the work area (like Python's auto_select_first_image).
   */
  autoSelectFirstImage(): void {
    if (this.levelState?.images?.length) {
      const firstImage = this.levelState.images[0];
      this.setCurrentImage(firstImage.id);
      this.selectedThumbnailIndex = 0;
      this.debugLog.emit(`Level ${this.level}: Auto-selected first image: ${firstImage.filename}`);
      // Force change detection to update view
      this.cdr.detectChanges();
    }
  }
  
  /**
   * Clear current selection when level is emptied (like Python's clear_navigation)
   */
  clearCurrentSelection(): void {
    this.currentImageId = null;
    this.currentImage = null;
    this.selectedThumbnailIndex = 0;
    this.markedLines = [];
    this.markMode = null;
    this.cropMode = false;
    this.debugLog.emit(`Level ${this.level}: Cleared selection`);
  }
  
  setCurrentImage(imageId: string): void {
    this.currentImageId = imageId;
    this.currentImage = this.levelState?.images.find(img => img.id === imageId) || null;
    // Clear marked lines when switching images (like Python UI)
    this.markedLines = [];
    this.debugLog.emit(`Level ${this.level}: Viewing image ${imageId}`);  
  }
  
  onThumbnailClick(image: ImageInfo, index: number): void {
    this.selectedThumbnailIndex = index;
    this.setCurrentImage(image.id);
    this.paneFocused.emit('nav');
  }
  
  onThumbnailImageClick(image: ImageInfo): void {
    // Clicking the thumbnail image shows it in the image viewer
    this.setCurrentImage(image.id);
    this.paneFocused.emit('work');
  }
  
  onFilenameClick(image: ImageInfo): void {
    // Clicking the filename opens the image in Windows viewer
    if (image?.id) {
      this.openInWindowsViewer.emit(image.id);
    }
  }
  
  onDeleteImage(image: ImageInfo): void {
    // Delete a single image via the delete button on thumbnail
    if (image?.id) {
      this.deleteImageRequested.emit(image.id);
    }
  }
  
  onExcludeImage(image: ImageInfo): void {
    // Remove image from selection (exclude from join operation)
    if (image?.id && this.levelState?.selectedImages?.includes(image.id)) {
      // Remove from selectedImages
      const idx = this.levelState.selectedImages.indexOf(image.id);
      if (idx > -1) {
        this.levelState.selectedImages.splice(idx, 1);
        this.debugLog.emit(`[EXCLUDE] Level ${this.level}: Excluded "${image.filename || image.id}" from selection`);
      }
    }
  }
  
  onOpenExternal(image: ImageInfo): void {
    // Open image in external viewer/editor
    if (image?.id) {
      this.openInWindowsViewer.emit(image.id);
    }
  }
  
  onPreviewImage(image: ImageInfo): void {
    // Open image in new tab for preview
    if (image?.url) {
      window.open(image.url, '_blank');
      this.debugLog.emit(`[PREVIEW] Level ${this.level}: Opened "${image.filename || image.id}" in new tab`);
    }
  }
  
  onThumbnailDoubleClick(image: ImageInfo): void {
    this.setCurrentImage(image.id);
    this.paneFocused.emit('work');
  }
  
  onSelectionChange(imageId: string, selected: boolean): void {
    // Selection is handled by the service through the parent component
    this.debugLog.emit(`Level ${this.level}: Image ${imageId} selection: ${selected}`);
  }
  
  isSelected(imageId: string): boolean {
    return this.levelState?.selectedImages.includes(imageId) || false;
  }
  
  getSelectionOrder(imageId: string): number {
    const index = this.levelState?.selectedImages.indexOf(imageId);
    return index !== undefined && index >= 0 ? index + 1 : 0;
  }
  
  // ==================== File Operations ====================
  
  openFileDialog(): void {
    this.fileInput.nativeElement.click();
  }
  
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.filesDropped.emit(input.files);
      input.value = ''; // Reset for next selection
    }
  }
  
  // ==================== Drag & Drop ====================
  
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }
  
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }
  
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.filesDropped.emit(event.dataTransfer.files);
    }
  }
  
  // ==================== Resize ====================
  
  startResize(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.navPaneWidth;
    
    document.addEventListener('mousemove', this.onResize);
    document.addEventListener('mouseup', this.stopResize);
  }
  
  private onResize = (event: MouseEvent): void => {
    if (!this.isResizing) return;
    
    const container = this.navPane?.nativeElement?.parentElement;
    if (!container) return;
    
    const containerWidth = container.clientWidth;
    const deltaX = event.clientX - this.startX;
    const deltaPercent = (deltaX / containerWidth) * 100;
    
    const newWidth = Math.max(15, Math.min(50, this.startWidth + deltaPercent));
    this.navPaneResized.emit(newWidth);
    this.cdr.detectChanges();
  };
  
  private stopResize = (): void => {
    this.isResizing = false;
    document.removeEventListener('mousemove', this.onResize);
    document.removeEventListener('mouseup', this.stopResize);
  };

  // ==================== Pin Feature (Level 1) ====================
  
  togglePin(): void {
    this.isPinned = !this.isPinned;
    this.pinToggled.emit(this.isPinned);
    this.debugLog.emit(`Level ${this.level}: Pin ${this.isPinned ? 'enabled' : 'disabled'}`);
  }

  // ==================== Select All ====================
  
  get allSelected(): boolean {
    if (!this.levelState?.images?.length) return false;
    return this.levelState.selectedImages.length === this.levelState.images.length;
  }
  
  get someSelected(): boolean {
    if (!this.levelState?.images?.length) return false;
    return this.levelState.selectedImages.length > 0 && 
           this.levelState.selectedImages.length < this.levelState.images.length;
  }
  
  toggleSelectAll(checked: boolean): void {
    this.debugLog.emit(`Level ${this.level}: Select all: ${checked}`);
    this.selectAllChanged.emit(checked);
  }
  
  // ==================== Mark/Cut Modes ====================
  
  setMarkMode(mode: 'vertical' | 'horizontal'): void {
    if (this.markMode === mode) {
      this.markMode = null;
      this.debugLog.emit(`[MARK] Level ${this.level}: Mark mode disabled`);
    } else {
      this.markMode = mode;
      this.cropMode = false;
      this.panMode = false;
      this.debugLog.emit(`[MARK] Level ${this.level}: Mark mode set to ${mode}`);
    }
  }
  
  onLineMarked(line: { type: 'vertical' | 'horizontal'; position: number }): void {
    this.markedLines.push({ position: line.position, type: line.type });
    this.debugLog.emit(`[MARK] Level ${this.level}: Marked ${line.type} line at position ${line.position} (total: ${this.markedLines.length})`);
  }
  
  get hasMarkedLines(): boolean {
    return this.markedLines.length > 0;
  }
  
  /**
   * Check if cut operation can be performed.
   * Like Python UI, cut is only enabled when:
   * 1. User has selected vertical or horizontal mark mode
   * 2. User has marked at least one line
   */
  get canCut(): boolean {
    return this.hasMarkedLines && this.markMode !== null;
  }
  
  cutLines(): void {
    // Only allow cut if mark mode is active and lines are marked (like Python UI)
    if (!this.canCut) {
      this.debugLog.emit(`[CUT] Level ${this.level}: Cannot cut - mark mode not active or no lines marked`);
      return;
    }
    
    if (this.currentImageId && this.markedLines.length > 0) {
      // Get all line positions (all should be same type - vertical or horizontal)
      const cutType = this.markedLines[0].type;
      const positions = this.markedLines.map(line => line.position);
      this.debugLog.emit(`[CUT] Level ${this.level}: Executing ${cutType} cut at ${positions.length} position(s): ${positions.join(', ')}`);
      
      // Emit cut with all line positions
      this.cutRequested.emit({ 
        imageId: this.currentImageId, 
        cutLines: positions,
        cutType: cutType 
      });
      this.markedLines = [];
      // Exit mark mode after cut (like Python UI)
      this.markMode = null;
    } else {
      this.debugLog.emit(`[CUT] Level ${this.level}: No image selected or no lines marked`);
    }
  }
  
  clearLines(): void {
    const count = this.markedLines.length;
    this.markedLines = [];
    this.debugLog.emit(`[MARK] Level ${this.level}: Cleared ${count} line(s)`);
  }  
  // ==================== Crop Mode ====================
  
  toggleCropMode(): void {
    this.cropMode = !this.cropMode;
    if (this.cropMode) {
      this.markMode = null;
      this.panMode = false;
      this.debugLog.emit(`[CROP] Level ${this.level}: Crop mode enabled`);
    } else {
      this.debugLog.emit(`[CROP] Level ${this.level}: Crop mode disabled`);
    }
  }

  // ==================== Pan Mode ====================
  
  togglePanMode(): void {
    this.panMode = !this.panMode;
    if (this.panMode) {
      this.markMode = null;
      this.cropMode = false;
    }
    this.debugLog.emit(`Level ${this.level}: Pan mode ${this.panMode ? 'enabled' : 'disabled'}`);
  }

  // ==================== Zoom Controls ====================
  
  zoomIn(): void {
    this.zoomLevel = Math.min(5.0, this.zoomLevel * 1.25);
    this.debugLog.emit(`Level ${this.level}: Zoom in to ${(this.zoomLevel * 100).toFixed(0)}%`);
  }
  
  zoomOut(): void {
    this.zoomLevel = Math.max(0.1, this.zoomLevel / 1.25);
    this.debugLog.emit(`Level ${this.level}: Zoom out to ${(this.zoomLevel * 100).toFixed(0)}%`);
  }
  
  resetZoom(): void {
    this.zoomLevel = 1.0;
    this.debugLog.emit(`Level ${this.level}: Zoom reset to 100%`);
  }

  // ==================== Preview Join ====================
  
  previewJoin(): void {
    this.debugLog.emit(`Level ${this.level}: Preview join requested`);
    // Preview logic would show a modal with the join preview
  }

  onCutLine(cutLine: number): void {
    if (this.currentImageId) {
      const filename = this.currentImageId.split(/[\\/]/).pop();
      // Default to horizontal cut when cutting via the direct draw method (left click without mark mode)
      const cutType = 'horizontal';
      this.debugLog.emit(`[CUT] Level ${this.level}: Direct ${cutType} cut at Y=${cutLine} for ${filename}`);
      this.cutRequested.emit({ imageId: this.currentImageId, cutLines: [cutLine], cutType });
    } else {
      this.debugLog.emit(`[CUT] Level ${this.level}: ERROR - No image selected for cut`);
    }
  }
  
  onCropRect(cropRect: { x: number; y: number; width: number; height: number }): void {
    if (this.currentImageId) {
      const filename = this.currentImageId.split(/[\\/]/).pop();
      this.debugLog.emit(`[CROP] Level ${this.level}: Cropping ${filename} at (${Math.round(cropRect.x)}, ${Math.round(cropRect.y)}) size ${Math.round(cropRect.width)}x${Math.round(cropRect.height)}`);
      this.cropRequested.emit({ imageId: this.currentImageId, cropRect });
      this.cropMode = false;
    } else {
      this.debugLog.emit(`[CROP] Level ${this.level}: ERROR - No image selected for crop`);
    }
  }
  
  joinVertical(): void {
    this.joinRequested.emit('vertical');
  }
  
  joinHorizontal(): void {
    this.joinRequested.emit('horizontal');
  }
  
  openSnagit(): void {
    // Open current image in Snagit (or external editor)
    if (this.currentImageId && this.levelState) {
      const image = this.levelState.images.find(img => img.id === this.currentImageId);
      if (image) {
        this.debugLog.emit(`[SNAGIT] Level ${this.level}: Opening in Snagit: ${image.filename}`);
        // Emit event to open in Snagit/external editor
        this.openInWindowsViewer.emit(this.currentImageId);
      }
    }
  }
  
  // ==================== Keyboard Navigation ====================
  
  onNavPaneKeydown(event: KeyboardEvent): void {
    if (!this.levelState?.images?.length) return;
    
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.navigateThumbnail(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.navigateThumbnail(1);
        break;
      case 'Home':
        event.preventDefault();
        this.selectedThumbnailIndex = 0;
        this.setCurrentImage(this.levelState.images[0].id);
        break;
      case 'End':
        event.preventDefault();
        this.selectedThumbnailIndex = this.levelState.images.length - 1;
        this.setCurrentImage(this.levelState.images[this.selectedThumbnailIndex].id);
        break;
      case ' ':
        event.preventDefault();
        // Toggle selection of current thumbnail
        if (this.currentImageId) {
          this.onSelectionChange(this.currentImageId, !this.isSelected(this.currentImageId));
        }
        break;
    }
  }
  
  private navigateThumbnail(delta: number): void {
    if (!this.levelState?.images?.length) return;
    
    const newIndex = Math.max(0, Math.min(
      this.levelState.images.length - 1,
      this.selectedThumbnailIndex + delta
    ));
    
    if (newIndex !== this.selectedThumbnailIndex) {
      this.selectedThumbnailIndex = newIndex;
      this.setCurrentImage(this.levelState.images[newIndex].id);
    }
  }
  
  // ==================== Status Indicators ====================
  
  get dbStatus(): 'saved' | 'modified' | 'none' {
    if (!this.levelState) return 'none';
    // Check if saved to DB
    return this.levelState.savedToDb ? 'saved' : 'modified';
  }
  
  get gcsStatus(): 'uploaded' | 'pending' | 'none' {
    if (!this.levelState) return 'none';
    return this.levelState.uploadedToGcs ? 'uploaded' : 'pending';
  }
  
  get canUndo(): boolean {
    return (this.levelState?.undoStack?.length || 0) > 0;
  }
  
  get canRedo(): boolean {
    return (this.levelState?.redoStack?.length || 0) > 0;
  }
  
  get selectedCount(): number {
    return this.levelState?.selectedImages?.length || 0;
  }
  
  get imageCount(): number {
    return this.levelState?.images?.length || 0;
  }

  /**
   * Get the current image index (1-based) for display
   */
  get currentImageIndex(): number {
    if (!this.currentImageId || !this.levelState?.images) return 0;
    const index = this.levelState.images.findIndex(img => img.id === this.currentImageId);
    return index >= 0 ? index + 1 : 0;
  }
  
  // ==================== UI Helpers ====================
  
  getLevelColor(): string {
    switch (this.level) {
      case 1: return '#2196f3';
      case 2: return '#4caf50';
      case 3: return '#ff9800';
      default: return '#9e9e9e';
    }
  }
  
  trackByImageId(index: number, image: ImageInfo): string {
    return image.id;
  }
}
